/**
 * components/help/HelpCategoryList.jsx — Phase 84.15
 *
 * Left column: renders the six categories grouped by `section_label`
 * (Figma pattern: DAILY / MONEY / LONG VIEW caps headers). Highlights
 * the active category, and inside it the active article.
 */
import React from 'react';
import * as icons from 'lucide-react';

function CategoryIcon({ name, className }) {
  const Comp = (name && icons[name]) || icons.BookOpen;
  return <Comp className={className} />;
}

export default function HelpCategoryList({ manifest, activeCategorySlug, activeArticleSlug, onSelect }) {
  if (!manifest?.categories?.length) return null;

  // Group by section_label to reproduce the Figma "labelled sections" grouping.
  const groups = new Map();
  for (const c of manifest.categories) {
    const key = c.section_label || '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(c);
  }

  return (
    <nav className="space-y-6" aria-label="Help topics" data-testid="help-category-list">
      {Array.from(groups.entries()).map(([sectionLabel, cats]) => (
        <div key={sectionLabel || 'default'}>
          {sectionLabel ? (
            <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-2 px-1">
              {sectionLabel}
            </div>
          ) : null}
          <div className="space-y-1">
            {cats.map((cat) => {
              const isActiveCat = cat.slug === activeCategorySlug;
              return (
                <div key={cat.slug}>
                  <button
                    type="button"
                    onClick={() => onSelect(cat.slug, cat.articles?.[0]?.slug)}
                    data-testid={`help-cat-${cat.slug}`}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                      isActiveCat ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted/60 text-foreground'
                    }`}
                  >
                    <CategoryIcon name={cat.icon} className="h-4 w-4 shrink-0" />
                    <span className="truncate">{cat.label}</span>
                  </button>
                  {isActiveCat && (cat.articles || []).length > 0 && (
                    <ul className="mt-1 mb-2 ml-4 border-l border-border/60 pl-3 space-y-0.5">
                      {cat.articles.map((a) => {
                        const isActiveArt = a.slug === activeArticleSlug;
                        return (
                          <li key={a.slug}>
                            <button
                              type="button"
                              onClick={() => onSelect(cat.slug, a.slug)}
                              data-testid={`help-art-${a.slug}`}
                              className={`w-full text-left text-[13px] px-2 py-1.5 rounded-md transition-colors ${
                                isActiveArt ? 'text-primary font-medium bg-primary/5' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                              }`}
                            >
                              {a.title}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}
